/**
 * Controlli e validazione della configurazione (CF03/CF04) — CL02.
 *
 * Due cose vivono qui, entrambe pure:
 *
 * - **la tabella dei controlli**: quale parametro espone ciascuno dei 15 tipi.
 *   Una tabella incompleta produce un configuratore a cui manca un controllo,
 *   e il progettista non ha modo di saperlo.
 * - **la validazione**: gli errori che il progettista deve vedere *mentre*
 *   configura, associati al controllo che li causa, invece che al salvataggio
 *   con un messaggio del server lontano dalla causa.
 *
 * Le regole rispecchiano i DTO del contratto
 * (`backend_statistiche/app/schemas/custom_fields.py`). La UI previene gli
 * errori comuni; la validazione definitiva resta del server.
 */

import type { FieldType } from "model/CustomFields";
import { codePointLength } from "./legacyAdapter";

/** I limiti del contratto, gli stessi che applica il server. */
export const LIMITS = {
  maxFieldsPerModel: 200,
  maxOptionsPerField: 500,
  maxLabelCodePoints: 120,
  maxTextLength: 10_000,
  maxTextareaLength: 50_000,
  textareaRows: { min: 2, max: 20 },
  numericPrecision: { min: 0, max: 8 },
  sequencePadding: { min: 3, max: 8 },
  expressionLength: 4096,
} as const;

/**
 * I parametri che ciascun tipo espone nel suo configuratore.
 *
 * I nomi sono quelli del contratto (`config.placeholder`, `config.precision`,
 * …): il configuratore li rende e non li rinomina, così la bozza che il
 * progettista vede è quella che il server riceve.
 */
export const FIELD_CONTROLS: Readonly<Record<FieldType, readonly string[]>> = {
  text: ["placeholder", "minLength", "maxLength"],
  number: ["precision", "min", "max"],
  date: ["displayFormat", "includeTime", "futureOnly"],
  boolean: ["trueLabel", "falseLabel", "default"],
  select: ["options", "default"],
  multiselect: ["options", "default"],
  radio: ["options", "default", "orientation"],
  autoincrement: ["registryId"],
  // `outputType` non sta qui: il tipo del risultato lo legge il server dalla
  // formula, e il configuratore lo mostra invece di chiederlo. Il valore resta
  // nel config — il contratto delle schema-commands lo pretende — e lo scrive
  // l'editor con quello che l'anteprima ha risposto.
  calculated: ["expression", "precision"],
  textarea: ["rows", "maxLength", "showCounter"],
  currency: [
    "currencyCode",
    "symbol",
    "symbolPosition",
    "decimalSeparator",
    "thousandsSeparator",
    "precision",
    "min",
    "max",
  ],
  percent: ["displayMode", "min", "max", "precision"],
  email: ["allowedDomain"],
  url: ["httpsOnly"],
  phone: ["prefix"],
  // Un solo parametro: se il file si può rinominare dalla scheda del task.
  // I limiti dell'allegato non sono del campo — formati e dimensione li decide
  // l'applicazione (`SUPPORTED_EXTENSIONS`, il server), e la subscription decide
  // quanto spazio c'è: un progettista che scegliesse "10 MB" qui prometterebbe
  // una cosa che il server non ha accettato.
  attachment: ["allowRename"],
};

/** I tipi che espongono un editor di opzioni. */
export const CHOICE_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
  "select",
  "multiselect",
  "radio",
]);

/** I tipi che accettano un limite minimo e massimo numerico. */
export const RANGE_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
  "number",
  "currency",
  "percent",
]);

/**
 * Verifica una configurazione e restituisce le chiavi dei problemi.
 *
 * Chiavi, non testi: il messaggio lo compone il catalogo di traduzioni. Un
 * errore qui non blocca il salvataggio — lo fa il server — ma il configuratore
 * lo mostra accanto al controllo che lo causa.
 */
export function validateFieldConfig(
  type: FieldType,
  config: Record<string, unknown>
): string[] {
  const issues: string[] = [];

  if (CHOICE_TYPES.has(type)) issues.push(...validateChoice(config));

  if (type === "text" || type === "textarea") {
    const max = config.maxLength;
    const min = config.minLength;
    if (typeof min === "number" && typeof max === "number" && min > max) {
      issues.push("custom_fields.error.invalid_range");
    }
    // I limiti del contratto, non un numero inventato dal client.
    if (type === "text" && typeof max === "number" && max > LIMITS.maxTextLength) {
      issues.push("custom_fields.error.validation_failed");
    }
  }

  if (type === "textarea" && typeof config.rows === "number") {
    const { min, max } = LIMITS.textareaRows;
    if (config.rows < min || config.rows > max) {
      issues.push("custom_fields.error.invalid_range");
    }
  }

  if (typeof config.precision === "number") {
    const { min, max } = LIMITS.numericPrecision;
    if (config.precision < min || config.precision > max) {
      issues.push("custom_fields.error.invalid_range");
    }
  }

  if (RANGE_TYPES.has(type)) {
    const { min, max } = config;
    // Confronto fra stringhe decimali canoniche: portarli a `Number` qui
    // reintrodurrebbe il float che il contratto evita.
    if (typeof min === "string" && typeof max === "string" && compareDecimals(min, max) > 0) {
      issues.push("custom_fields.error.invalid_range");
    }
  }

  if (type === "percent" && typeof config.displayFactor === "string") {
    // Un fattore non positivo non è una presentazione: è una divisione per zero
    // che aspetta il primo valore.
    if (compareDecimals(config.displayFactor, "0") <= 0) {
      issues.push("custom_fields.error.invalid_range");
    }
  }

  if (type === "calculated") {
    const expression = config.expression;
    if (typeof expression !== "string" || expression.trim() === "") {
      issues.push("custom_fields.error.expression_required");
      return issues;
    }
    if (typeof expression === "string" && expression.length > LIMITS.expressionLength) {
      issues.push("custom_fields.formula.limit_exceeded");
    }
  }

  if (type === "autoincrement" && !config.registryId) {
    // Un progressivo senza registro non può produrre un codice.
    issues.push("custom_fields.error.registry_required");
  }

  return issues;
}

/**
 * Lo stesso verdetto di `validateFieldConfig`, ma con il **nome del parametro**
 * che lo causa.
 *
 * L'elenco piatto basta a bloccare il salvataggio e non basta a correggerlo: un
 * `validation_failed` senza soggetto, mostrato una volta sola, dice che qualcosa
 * non va e niente su cosa. Qui il problema si attacca al controllo che lo
 * produce, così il messaggio compare sotto il campo giusto e il campo si può
 * evidenziare. La validazione resta una: questa non aggiunge regole, le
 * attribuisce.
 */
export function configProblemsByParam(
  type: FieldType,
  config: Record<string, unknown>
): Record<string, {
  key: string;
  fallback: string;
  /**
   * I parametri del messaggio, quando esistono: `custom_fields.error.invalid_range`
   * porta `{min}` e `{max}` nel catalogo, e i limiti li conosce solo chi fa la
   * diagnosi. Chi rende la chiave senza di questi mostrerebbe i segnaposto alla
   * lettera (vedi `translateMessage`).
   */
  params?: Record<string, string | number>;
}> {
  const problems: Record<string, { key: string; fallback: string; params?: Record<string, string | number> }> = {};
  const add = (
    name: string,
    key: string,
    fallback: string,
    params?: Record<string, string | number>
  ) => {
    // Il primo problema di un parametro è quello da correggere: gli altri
    // scompaiono con lui, e impilarli sotto un campo stretto li rende illeggibili.
    if (!problems[name]) problems[name] = params ? { key, fallback, params } : { key, fallback };
  };

  if (type === "calculated") {
    const expression = config.expression;
    if (typeof expression !== "string" || expression.trim() === "") {
      add("expression", "custom_fields.error.expression_required",
        "Scrivi l'espressione da calcolare. Senza, il campo non può produrre un valore.");
    } else if (expression.length > LIMITS.expressionLength) {
      add("expression", "custom_fields.formula.limit_exceeded",
        `L'espressione supera i ${LIMITS.expressionLength} caratteri ammessi.`);
    }
    // Il tipo del risultato non si pretende: non lo sceglie piu' il progettista,
    // lo legge il server dalla formula. Chiederlo qui sarebbe una richiesta che
    // il configuratore non puo' piu' soddisfare.
  }

  if (type === "autoincrement" && !config.registryId) {
    add("registryId", "custom_fields.error.registry_required",
      "Scegli il registro che assegna i numeri. Senza, il campo non ha un progressivo.");
  }

  if (CHOICE_TYPES.has(type)) {
    const options = Array.isArray(config.options) ? config.options : [];
    if (options.length === 0) {
      add("options", "custom_fields.error.options_required",
        "Aggiungi almeno una voce: un campo di scelta senza voci non offre niente.");
    }
  }

  if (typeof config.precision === "number") {
    const { min, max } = LIMITS.numericPrecision;
    if (config.precision < min || config.precision > max) {
      add("precision", "custom_fields.error.invalid_range",
        `I decimali devono stare fra ${min} e ${max}.`);
    }
  }

  if (type === "textarea" && typeof config.rows === "number") {
    const { min, max } = LIMITS.textareaRows;
    if (config.rows < min || config.rows > max) {
      add("rows", "custom_fields.error.invalid_range",
        `Le righe devono stare fra ${min} e ${max}.`);
    }
  }

  if (type === "text" || type === "textarea") {
    const min = config.minLength;
    const max = config.maxLength;
    if (typeof min === "number" && typeof max === "number" && min > max) {
      add("minLength", "custom_fields.error.invalid_range",
        "La lunghezza minima non può superare la massima.");
    }
    if (type === "text" && typeof max === "number" && max > LIMITS.maxTextLength) {
      add("maxLength", "custom_fields.error.invalid_range",
        `La lunghezza massima non può superare ${LIMITS.maxTextLength}.`);
    }
  }

  if (RANGE_TYPES.has(type)) {
    const min = config.min;
    const max = config.max;
    // Confronto fra decimali canonici, come nell'elenco piatto: qui cambia solo
    // a quale campo il problema si attacca.
    if (typeof min === "string" && typeof max === "string" && compareDecimals(min, max) > 0) {
      add("max", "custom_fields.error.invalid_range",
        "Il massimo non può essere minore del minimo.",
        { min, max });
    }
  }

  return problems;
}

function validateChoice(
  config: Record<string, unknown>
): string[] {
  const issues: string[] = [];
  const options = Array.isArray(config.options) ? config.options : [];

  if (options.length === 0) {
    issues.push("custom_fields.error.validation_failed");
    return issues;
  }
  if (options.length > LIMITS.maxOptionsPerField) {
    issues.push("custom_fields.error.validation_failed");
  }

  const ids = options.map((option) => String((option as { id?: unknown })?.id ?? ""));
  if (ids.some((id) => id.trim() === "") || new Set(ids).size !== ids.length) {
    // Le chiavi identificano stabilmente le opzioni.
    issues.push("custom_fields.error.duplicate_option");
  }

  const available = new Set(ids);
  const declared = config.default;
  const missing = Array.isArray(declared)
    ? declared.some((id) => !available.has(String(id)))
    : declared !== undefined && declared !== null && !available.has(String(declared));
  if (missing) {
    // Un default che punta a un'opzione rimossa rende il campo inutilizzabile
    // al primo utilizzo.
    issues.push("custom_fields.error.invalid_default");
  }

  return issues;
}

/**
 * Confronta due decimali canonici senza passare da `Number`.
 *
 * Allineati per parte intera e frazionaria e confrontati come stringhe: è più
 * codice di `Number(a) - Number(b)` e non introduce un float in un dominio dove
 * i soldi non lo ammettono.
 */
export function compareDecimals(left: string, right: string): number {
  const a = normalise(left);
  const b = normalise(right);

  // Il segno si tratta a parte: confrontare le stringhe funziona per i positivi
  // e mente per i negativi (`"2" > "1"`, quindi `-2` risulterebbe maggiore di
  // `-1`). Un intervallo invertito verrebbe accettato.
  if (a.negative !== b.negative) return a.negative ? -1 : 1;

  const magnitude = compareMagnitude(a, b);
  return a.negative ? -magnitude : magnitude;
}

function compareMagnitude(
  a: { int: string; frac: string },
  b: { int: string; frac: string }
): number {
  if (a.int.length !== b.int.length) return a.int.length < b.int.length ? -1 : 1;
  if (a.int !== b.int) return a.int < b.int ? -1 : 1;

  const width = Math.max(a.frac.length, b.frac.length);
  const left = a.frac.padEnd(width, "0");
  const right = b.frac.padEnd(width, "0");
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

function normalise(value: string): { negative: boolean; int: string; frac: string } {
  const negative = value.trim().startsWith("-");
  const [int, frac = ""] = value.trim().replace(/^-/, "").split(".");
  const trimmedInt = int.replace(/^0+(?=\d)/, "");
  // `-0` e `0` sono lo stesso numero, quindi la parte intera nulla non è mai
  // negativa.
  return {
    negative: negative && trimmedInt !== "0",
    int: trimmedInt,
    frac,
  };
}

/**
 * La label sta nei limiti?
 *
 * Misurata in code point: un'emoji è un carattere per il server e due unità
 * UTF-16 per `String.length`, quindi contare l'unità sbagliata rifiuterebbe una
 * label che il server accetta.
 */
export const labelFits = (label: string): boolean =>
  codePointLength(label) <= LIMITS.maxLabelCodePoints;
